<template>
  <div class="use-case">
    <div class="use-case__box">
      <header class="use-case__box__header">
        <ui-button
          class="use-case__box__header__close"
          @click.stop="toggleSort"
          button="secondary"
          :label="$t('useCase.sort.tooltip')"
          :icon-only="true"
          icon="filter"
          v-tooltip="{
            placement: 'top',
            trigger: 'hover click',
            content: $t('useCase.sort.tooltip'),
            offset: 3,
          }"
        >
        </ui-button>
        <div class="use-case__box__header__title">
          {{ $t('useCase.title') }}
          <span
            class="backoffice-icons use-case__box__header__title__icon"
            v-tooltip="{
              placement: 'top',
              trigger: 'hover click',
              content: $t('useCase.tooltip'),
              offset: 3,
            }"
          >
            info
          </span>
        </div>
        <transition name="fade">
          <div class="use-case__box__header__sort" v-if="sort.active" @click.stop>
            <div class="use-case__box__header__sort__title">
              {{ $t('useCase.sort.title') }}
            </div>
            <ui-checkbox
              class="use-case__box__header__sort__checkbox"
              :id="`activity-${u.key}`"
              v-for="u in uniqUseCases"
              :key="u.key"
              v-model="model"
              :value="u.key"
            >
              {{ u.name[$i18n.locale] || u.name.default }}
            </ui-checkbox>
          </div>
        </transition>
      </header>
      <div class="use-case__box__container">
        <ul class="use-case__box__container__list">
          <li
            class="use-case__box__container__list__item"
            :class="{
              'use-case__box__container__list__item--active': currentUseCase && useCase.key === currentUseCase.key,
            }"
            v-for="(useCase, idx) in filteredUseCases"
            :key="`usecase-${idx}`"
            @click="toggleCurrentUseCase(useCase)"
          >
            <div class="use-case__box__container__list__item__color" :style="{ backgroundColor: useCase.color }"></div>
            <div class="use-case__box__container__list__item__content">
              <div class="use-case__box__container__list__item__content__title">
                {{ useCase.title[$i18n.locale] || useCase.title.default }}
              </div>
              <div class="use-case__box__container__list__item__content__description">
                {{ useCase.description[$i18n.locale] || useCase.description.default }}
              </div>
            </div>
            <div class="use-case__box__container__list__item__score">
              <div class="use-case__box__container__list__item__score__value">
                {{ useCase.note }}
              </div>
              <img
                :src="require('@/assets/img/ratings/star-full.svg')"
                class="use-case__box__container__list__item__score__img"
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import UiButton from '@/components/UI/Button.vue'
import UiCheckbox from '@/components/UI/Checkbox.vue'
import { uniqBy } from 'lodash'

export default {
  name: 'UseCase',
  components: {
    UiButton,
    UiCheckbox,
  },
  computed: {
    ...mapState({
      currentUseCase: state => state.data.currentUseCase,
      useCases: state => state.data.useCases,

      sort: state => state.bot.sort,

      activeUseCases: state => state.refine.activeUseCases,
    }),
    uniqUseCases() {
      return uniqBy(this.useCases, 'activity.key').map(useCase => useCase.activity)
    },
    filteredUseCases() {
      return this.useCases.filter(useCase => this.activeUseCases.includes(useCase.activity.key))
    },
    model: {
      get() {
        return this.activeUseCases
      },
      set(array) {
        this.updateActiveUseCases(array)
      },
    },
  },
  methods: {
    ...mapActions({
      setCurrentUseCase: 'data/setCurrentUseCase',
      resetResponses: 'data/resetResponses',

      toggleSort: 'bot/toggleSort',
      toggleRefreshData: 'bot/toggleRefreshData',

      updateActiveUseCases: 'refine/updateActiveUseCases',
    }),
    toggleCurrentUseCase(useCase) {
      this.toggleRefreshData(true)
      this.setCurrentUseCase(useCase).then(() => {
        this.resetResponses()
        this.toggleRefreshData(false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.use-case {
  &__box {
    @include box;

    @media (min-width: $screen-sm) {
      width: 100%;
      height: 100%;
    }

    &__header {
      @include header;

      &__close {
        @include close;
      }

      &__title {
        @include title;

        display: flex;
        align-items: center;

        &__icon {
          margin-left: 12px;
        }
      }

      &__sort {
        @include box;

        position: absolute;
        top: 52px;
        right: 0;
        padding: $gutter-sm;
        max-height: initial;

        @media (min-width: $screen-sm) {
          box-shadow: 0 0 6px rgba($black, 0.1);
        }

        &__title {
          @include sub-title;
        }

        &__checkbox {
          margin-bottom: 10px;
          width: 100%;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    &__container {
      @include container;

      padding: 0;

      &__list {
        margin: 0;
        padding: 0;
        list-style: none;

        &__item {
          display: flex;
          align-items: center;
          border-bottom: 1px solid $generic-color-bg-disabled;
          padding: $gutter-sm;

          &:last-child {
            border-bottom: 0;
          }

          &:hover {
            background-color: rgba($button-color-bg-disabled, 0.5);
            cursor: pointer;
          }

          &--active {
            background-color: $button-color-bg-disabled;
          }

          &__color {
            margin-right: 12px;
            border-radius: 50%;
            width: 16px;
            height: 16px;
          }

          &__content {
            flex: 1;

            &__title {
              font-weight: bold;
            }

            &__description {
              opacity: 0.8;
              font-size: $font-size-sm;
            }
          }

          &__score {
            display: flex;
            align-items: center;
            margin-left: 12px;

            &__value {
              font-size: $font-size-title;
              font-weight: 800;
            }
          }
        }
      }
    }
  }
}
</style>
